import { isEmpty } from 'lodash';
import { useEffect } from 'react';

import { useAnalytics } from '@/hooks/analytics';
import { useSearchRecs, useSearchTrending } from '@/hooks/app/useSearch';

import { SearchStyledLoader } from '../styles';
import { TrendingAndRecentResults } from '../TrendingAndRecent';
import {
	SearchSuggestionsContainer,
	SearchSuggestionsList,
	SearchSuggestionsListItem,
	SearchSuggestionsListItemButton,
} from './styles';

export interface Props {
	input: string;
	debouncedInput: string;
	onSubmitQuery: (input: string) => void;
	recentSearches: string[];
	location: 'app' | 'meeting experience';
}

const SearchSuggestions: React.FC<Props> = ({
	input,
	debouncedInput,
	onSubmitQuery,
	recentSearches,
	location,
}) => {
	const { logEvent } = useAnalytics();
	const { searchRecs, isLoading: searchRecsLoading } = useSearchRecs(debouncedInput);
	const { isLoading: isTrendingLoading, terms: trendingTerms, error: trendingError } = useSearchTrending();
	const showTrendingAndRecents = isEmpty(searchRecs) || !input;

	useEffect(() => {
		if (!searchRecsLoading && !isTrendingLoading) {
			const recentAndTrendingResults = [
				...(recentSearches ?? []),
				...(trendingTerms?.map(({ term }) => term) ?? []),
			];
			const searchRecResults = [...(searchRecs?.map(({ term }) => term) ?? [])];
			const suggestionsShown = showTrendingAndRecents ? recentAndTrendingResults : searchRecResults;
			const type = showTrendingAndRecents ? 'trending' : 'dynamic_results';
			logEvent({
				eventName: 'Search : Suggestions : Shown',
				eventProps: {
					query: input,
					query_character_count: input.length,
					suggestions_shown: suggestionsShown,
					type,
					location: location,
				},
			});
		}
	}, [
		isTrendingLoading,
		searchRecsLoading,
		trendingTerms,
		showTrendingAndRecents,
		recentSearches,
		searchRecs,
		input,
		location,
		logEvent,
	]);

	// if there are no search recs, show trending and recent
	if (showTrendingAndRecents) {
		if (isTrendingLoading) {
			return <SearchStyledLoader />;
		}

		return (
			<>
				<TrendingAndRecentResults
					recentSearches={recentSearches}
					title="recentsTitle"
					onTermClick={onSubmitQuery}
				/>
				<TrendingAndRecentResults
					error={trendingError}
					trendingTerms={trendingTerms}
					title="trendingTitle"
					onTermClick={onSubmitQuery}
				/>
			</>
		);
	}

	if (searchRecsLoading) {
		return <SearchStyledLoader />;
	}

	const searchRecItems = searchRecs?.map(({ term }) => (
		<SearchSuggestionsListItem key={term}>
			<SearchSuggestionsListItemButton onClick={() => onSubmitQuery(term)}>
				{term}
			</SearchSuggestionsListItemButton>
		</SearchSuggestionsListItem>
	));

	return (
		<SearchSuggestionsContainer data-testid="search-recs">
			<SearchSuggestionsList>{searchRecItems}</SearchSuggestionsList>
		</SearchSuggestionsContainer>
	);
};

export default SearchSuggestions;
